import { icoContact, legalContact } from "../legalContact";

const cookiePolicy = {
  eyebrow: "Legal",
  title: "Cookie Policy",
  summary:
    "The cookies and similar technologies this website uses, and how you can control them.",
  effectiveDate: "1 July 2026",
  lastUpdated: "1 July 2026",
  intro: [
    {
      type: "paragraph",
      text: 'This Cookie Policy explains how AV Facade Services Ltd ("we", "our", or "us") uses cookies and similar technologies on this website.',
    },
    {
      type: "paragraph",
      text: [
        "It should be read alongside our ",
        { text: "Privacy Policy", to: "/privacypolicy" },
        ", which explains how we handle personal information more generally.",
      ],
    },
  ],
  sections: [
    {
      id: "who-we-are",
      title: "Who We Are",
      blocks: [{ type: "address" }],
    },
    {
      id: "what-are-cookies",
      title: "What Are Cookies",
      blocks: [
        {
          type: "paragraph",
          text: "Cookies are small text files placed on your device when you visit a website.",
        },
        {
          type: "paragraph",
          text: "They are widely used to make websites work correctly, to remember your preferences and to provide information to website owners about how their site is used.",
        },
        {
          type: "paragraph",
          text: "Similar technologies such as local storage, pixels and tags may also be used, and are covered by this policy.",
        },
      ],
    },
    {
      id: "types-of-cookies",
      title: "Types of Cookies We May Use",
      blocks: [
        {
          type: "subsection",
          title: "Strictly necessary cookies",
          blocks: [
            {
              type: "paragraph",
              text: "These are required for the website to function correctly, for example to maintain security and to remember choices such as light or dark display mode.",
            },
            {
              type: "paragraph",
              text: "These cookies cannot be switched off through our website.",
            },
          ],
        },
        {
          type: "subsection",
          title: "Functional cookies",
          blocks: [
            {
              type: "paragraph",
              text: "These allow the website to remember preferences you have made in order to provide a more consistent experience.",
            },
          ],
        },
        {
          type: "subsection",
          title: "Analytics and performance cookies",
          blocks: [
            {
              type: "paragraph",
              text: "These help us understand how visitors use our website, such as which pages are visited and how long visitors spend on them.",
            },
            {
              type: "paragraph",
              text: "This information is used only to improve our website.",
            },
          ],
        },
        {
          type: "subsection",
          title: "Third-party cookies",
          blocks: [
            {
              type: "paragraph",
              text: "Some pages may include content from third parties, such as embedded maps, videos or social media links.",
            },
            {
              type: "paragraph",
              text: "Where this happens, those third parties may set their own cookies. We do not control these cookies.",
            },
          ],
        },
      ],
    },
    {
      id: "cookies-we-currently-use",
      title: "Cookies We Currently Use",
      blocks: [
        {
          type: "paragraph",
          text: "Our website is designed to use as few cookies as possible.",
        },
        {
          type: "paragraph",
          text: "At present we use only strictly necessary and functional storage required for the website to work, including remembering your display mode preference.",
        },
        {
          // The single item the site actually stores — see COLOR_MODE_STORAGE_KEY in
          // src/theme.js. Keep this table in step with that constant.
          type: "table",
          caption: "Storage used by this website",
          columns: ["Name", "Type", "Purpose", "Duration"],
          rows: [
            [
              "av-facades-color-mode (local storage)",
              "Strictly necessary",
              "Remembers whether you have chosen the light or dark display mode",
              "Persists until you clear your browser storage",
            ],
          ],
        },
        {
          type: "paragraph",
          text: "We may introduce analytics cookies in future. Where we do, and where consent is required, you will be asked for your consent before non-essential cookies are placed.",
        },
      ],
    },
    {
      id: "analytics-tools",
      title: "Analytics Tools",
      blocks: [
        {
          type: "paragraph",
          text: "Where analytics are used, we may rely on services such as Google Analytics to understand how visitors use our website.",
        },
        { type: "paragraph", text: "These services may collect:" },
        {
          type: "list",
          items: [
            "IP address (anonymised where possible)",
            "Device information",
            "Browser information",
            "Pages visited",
            "Time spent on pages",
          ],
        },
        {
          type: "paragraph",
          text: "We do not use this information to identify individual visitors.",
        },
      ],
    },
    {
      id: "legal-basis",
      title: "Legal Basis",
      blocks: [
        {
          type: "paragraph",
          text: "Strictly necessary cookies are used on the basis of our legitimate interest in operating a secure and functional website.",
        },
        {
          type: "paragraph",
          text: "Non-essential cookies, including analytics and marketing cookies, will only be placed where you have given consent, in line with UK GDPR and the Privacy and Electronic Communications Regulations (PECR).",
        },
      ],
    },
    {
      id: "managing-cookies",
      title: "Managing Cookies",
      blocks: [
        {
          type: "paragraph",
          text: "You can control and delete cookies at any time through your browser settings.",
        },
        { type: "paragraph", text: "Most browsers allow you to:" },
        {
          type: "list",
          items: [
            "View the cookies stored on your device",
            "Delete individual or all cookies",
            "Block cookies from specific websites",
            "Block all cookies",
          ],
        },
        {
          type: "paragraph",
          text: "Please note that blocking or deleting cookies may affect how parts of this website function.",
        },
        {
          type: "paragraph",
          text: "Guidance for managing cookies is available in the help section of your browser.",
        },
      ],
    },
    {
      id: "withdrawing-consent",
      title: "Withdrawing Consent",
      blocks: [
        {
          type: "paragraph",
          text: "Where you have given consent to non-essential cookies, you may withdraw that consent at any time by clearing the cookies stored by this website in your browser and by adjusting your browser settings to block them.",
        },
      ],
    },
    {
      id: "third-party-websites",
      title: "Third-Party Websites",
      blocks: [
        { type: "paragraph", text: "Our website may contain links to other websites." },
        {
          type: "paragraph",
          text: "We are not responsible for the cookies or privacy practices of third-party websites and encourage you to read their cookie and privacy policies.",
        },
      ],
    },
    {
      id: "changes-to-this-policy",
      title: "Changes to This Policy",
      blocks: [
        {
          type: "paragraph",
          text: "We may update this Cookie Policy from time to time to reflect changes in the technologies we use or in applicable law.",
        },
        {
          type: "paragraph",
          text: "Any updates will be published on this page with a revised effective date.",
        },
      ],
    },
    {
      id: "contact-us",
      title: "Contact Us",
      blocks: [
        {
          type: "paragraph",
          text: "If you have any questions about this Cookie Policy, please contact:",
        },
        {
          type: "address",
          contact: { ...legalContact, lines: legalContact.lines.slice(0, 3) },
        },
      ],
    },
    {
      id: "complaints",
      title: "Complaints",
      blocks: [
        {
          type: "paragraph",
          text: "If you are unhappy with how we use cookies or handle your information, you have the right to lodge a complaint with the UK's supervisory authority:",
        },
        {
          type: "callout",
          title: icoContact.name,
          blocks: [
            {
              type: "paragraph",
              text: [
                "Website: ",
                { text: icoContact.website, href: icoContact.website, external: true },
              ],
            },
            {
              type: "paragraph",
              text: [
                "Telephone: ",
                { text: icoContact.telephone, href: icoContact.telephoneHref },
              ],
            },
          ],
        },
      ],
    },
  ],
};

export default cookiePolicy;
